var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * NotificationsContext
 * ====================
 * Owns the live SSE connection, the in-memory list, and the unread count.
 *
 * Wiring (see `App.tsx`):
 *   <AuthProvider> → <UserProvider> → <NotificationsProvider>
 *
 * The provider only opens the SSE stream once a user UUID is available.
 * On logout (userInfo cleared) it tears the stream down and resets state.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { useUser } from './UserContext';
import { notificationsApi, openNotificationsStream, } from '../api/notificationsApi';
const NotificationsContext = createContext({
    items: [],
    unreadCount: 0,
    connected: false,
    latestToast: null,
    clearLatestToast: () => undefined,
    markRead: () => __awaiter(void 0, void 0, void 0, function* () { return undefined; }),
    markAllRead: () => __awaiter(void 0, void 0, void 0, function* () { return undefined; }),
    clearAll: () => __awaiter(void 0, void 0, void 0, function* () { return undefined; }),
    refresh: () => __awaiter(void 0, void 0, void 0, function* () { return undefined; }),
    subscribe: () => () => undefined,
});
export const useNotifications = () => useContext(NotificationsContext);
/**
 * Convenience hook — subscribe to SSE notifications for the lifetime of the
 * calling component. The `predicate` and `listener` are captured in refs so
 * callers don't have to memoize them; the subscription is created once and
 * torn down on unmount.
 */
export function useNotificationSubscription(predicate, listener) {
    const { subscribe } = useContext(NotificationsContext);
    const predicateRef = useRef(predicate);
    const listenerRef = useRef(listener);
    predicateRef.current = predicate;
    listenerRef.current = listener;
    useEffect(() => {
        const unsubscribe = subscribe((n) => predicateRef.current(n), (n) => listenerRef.current(n));
        return unsubscribe;
    }, [subscribe]);
}
// ─── Implementation ───────────────────────────────────────────────────────────
const LAST_EVENT_ID_KEY = 'notifications:lastEventId';
export const NotificationsProvider = ({ children, }) => {
    var _a;
    const { userInfo } = useUser();
    const userId = (_a = userInfo === null || userInfo === void 0 ? void 0 : userInfo.userId) !== null && _a !== void 0 ? _a : null;
    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [connected, setConnected] = useState(false);
    const [latestToast, setLatestToast] = useState(null);
    const subscribersRef = useRef(new Set());
    // Live mirror of `items` so channels that need the current list (subscribe
    // backfill, connect-time reconciliation) can read it without waiting for a
    // re-render. Written from a single useEffect below so it stays in lockstep
    // with the committed React state.
    const itemsRef = useRef([]);
    // Highest notification id known at (or since) mount. Used to distinguish
    // "backlog" notifications (surfaced via REST refresh or SSE replay) from
    // genuinely live ones — the former must NOT pop a toast, or a page load
    // with several unread items would flood the user with stale snackbars.
    const mountWatermarkRef = useRef(null);
    /** Fire `listener` for one item against a single subscriber, honouring the
     *  subscriber's own seenIds dedupe + LRU cap. Extracted so both the live
     *  `fanout` path and the `subscribe`-time backfill share identical semantics.
     */
    const deliverToSubscriber = (sub, item) => {
        try {
            if (sub.seenIds.has(item.id))
                return;
            if (!sub.predicate(item))
                return;
            sub.seenIds.add(item.id);
            // Cap seenIds at 500 to avoid unbounded growth on long sessions.
            if (sub.seenIds.size > 500) {
                const first = sub.seenIds.values().next().value;
                if (first !== undefined)
                    sub.seenIds.delete(first);
            }
            sub.listener(item);
        }
        catch (err) {
            console.warn('[notifications] subscriber threw', err);
        }
    };
    const subscribe = useCallback((predicate, listener) => {
        const entry = { predicate, listener, seenIds: new Set() };
        subscribersRef.current.add(entry);
        // Backfill: replay the current in-memory items to the new subscriber so
        // it can reconcile against notifications that landed BEFORE it mounted
        // (e.g. user navigates from Dashboard to Reviews after a document has
        // finished processing). Oldest-first preserves causal ordering; the
        // subscriber's seenIds dedupes any subsequent live/replay delivery.
        const snapshot = itemsRef.current;
        for (let i = snapshot.length - 1; i >= 0; i--) {
            deliverToSubscriber(entry, snapshot[i]);
        }
        return () => {
            subscribersRef.current.delete(entry);
        };
    }, []);
    const fanout = useCallback((item) => {
        subscribersRef.current.forEach((sub) => deliverToSubscriber(sub, item));
    }, []);
    // Keep the latest id in a ref so the SSE callback can read it on reconnect.
    const lastEventIdRef = useRef(typeof window !== 'undefined'
        ? sessionStorage.getItem(LAST_EVENT_ID_KEY)
        : null);
    const setLastEventId = useCallback((id) => {
        const str = String(id);
        lastEventIdRef.current = str;
        try {
            sessionStorage.setItem(LAST_EVENT_ID_KEY, str);
        }
        catch (_a) {
            /* sessionStorage may be unavailable in some embedded contexts */
        }
    }, []);
    // ── Initial load ──────────────────────────────────────────────────────────
    const refresh = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!userId)
            return;
        try {
            const data = yield notificationsApi.list(50);
            setItems(data.items);
            setUnreadCount(data.unreadCount);
            if (data.items.length > 0) {
                setLastEventId(data.items[0].id);
                // First successful refresh establishes the "anything at-or-below this
                // id is backlog" watermark. Later SSE events / polls with id > this
                // watermark are considered live and may pop a toast.
                if (mountWatermarkRef.current === null) {
                    mountWatermarkRef.current = data.items[0].id;
                }
            }
            // Reconcile: feed every item through the subscriber pipeline. This is
            // the critical fix for the class of bug where a notification arrived
            // via REST (because SSE hadn't opened yet, or replay was suppressed by
            // Last-Event-ID) and card overlays / status flips never fired. Each
            // subscriber's seenIds dedupes, so repeat calls are safe. Oldest-first.
            for (let i = data.items.length - 1; i >= 0; i--) {
                fanout(data.items[i]);
            }
        }
        catch (err) {
            console.warn('[notifications] initial load failed', err);
        }
    }), [userId, setLastEventId, fanout]);
    useEffect(() => {
        if (!userId) {
            setItems([]);
            setUnreadCount(0);
            lastEventIdRef.current = null;
            mountWatermarkRef.current = null;
            try {
                sessionStorage.removeItem(LAST_EVENT_ID_KEY);
            }
            catch (_a) {
                /* ignored */
            }
            return;
        }
        void refresh();
    }, [userId, refresh]);
    // Keep itemsRef in lockstep with items so subscribe-time backfill and any
    // ref-based readers see the current list without an extra render tick.
    useEffect(() => {
        itemsRef.current = items;
    }, [items]);
    // ── Live stream ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!userId)
            return;
        const abort = openNotificationsStream({
            onOpen: () => {
                setConnected(true);
                // Do NOT call refresh() here — the initial load effect already
                // fetches on mount, and missed events since the last disconnect are
                // replayed by the server via Last-Event-ID, so a second REST call
                // is redundant and produces the duplicate notifications?limit=50
                // request visible in DevTools on every page load.
            },
            onError: () => setConnected(false),
            onMessage: (item) => {
                setLastEventId(item.id);
                // Was this item already known (from REST refresh or replay)? Used
                // below to gate the unreadCount increment so replay doesn't
                // double-count.
                const isNew = !itemsRef.current.some((p) => p.id === item.id);
                setItems((prev) => {
                    if (prev.some((p) => p.id === item.id))
                        return prev;
                    return [item, ...prev].slice(0, 200);
                });
                if (isNew && item.status === 'unread') {
                    setUnreadCount((c) => c + 1);
                }
                // Toast only for events strictly newer than the mount watermark
                // (i.e. genuinely live). Suppresses spam on SSE replay for backlog
                // items the user already sees in the bell from the initial REST
                // refresh.
                const isLive = mountWatermarkRef.current === null || item.id > mountWatermarkRef.current;
                if (isLive) {
                    setLatestToast({ id: item.id, notification: item });
                    if (mountWatermarkRef.current === null ||
                        item.id > mountWatermarkRef.current) {
                        mountWatermarkRef.current = item.id;
                    }
                }
                // Fan out to feature-level subscribers (Home cards, etc.) so they
                // can react to state-sync events without timer-based polling.
                fanout(item);
            },
        }, () => lastEventIdRef.current);
        return () => {
            abort();
            setConnected(false);
        };
    }, [userId, setLastEventId, fanout, refresh]);
    // Safety net: if SSE is disconnected, periodically re-sync from REST so
    // badge/panel catches up without a full page refresh.
    useEffect(() => {
        if (!userId || connected)
            return;
        const timer = window.setInterval(() => {
            void refresh();
        }, 15000);
        return () => window.clearInterval(timer);
    }, [userId, connected, refresh]);
    // ── Mutations ─────────────────────────────────────────────────────────────
    const markRead = useCallback((id) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const res = yield notificationsApi.markRead(id);
            setItems((prev) => prev.map((n) => n.id === id ? Object.assign(Object.assign({}, n), { status: 'read', readAt: new Date().toISOString() }) : n));
            setUnreadCount(res.unreadCount);
        }
        catch (err) {
            console.warn('[notifications] markRead failed', err);
        }
    }), []);
    const markAllRead = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield notificationsApi.markAllRead();
            setItems((prev) => prev.map((n) => n.status === 'unread'
                ? Object.assign(Object.assign({}, n), { status: 'read', readAt: new Date().toISOString() }) : n));
            setUnreadCount(0);
        }
        catch (err) {
            console.warn('[notifications] markAllRead failed', err);
        }
    }), []);
    const clearAll = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        // Optimistic clear so both surfaces (bell + dashboard panel) update
        // instantly. Snapshot lets us roll back on backend failure.
        let prevItems = [];
        let prevUnread = 0;
        setItems((curr) => { prevItems = curr; return []; });
        setUnreadCount((curr) => { prevUnread = curr; return 0; });
        try {
            yield notificationsApi.deleteAll();
        }
        catch (err) {
            console.warn('[notifications] clearAll failed — rolling back', err);
            setItems(prevItems);
            setUnreadCount(prevUnread);
        }
    }), []);
    const clearLatestToast = useCallback(() => setLatestToast(null), []);
    const value = useMemo(() => ({
        items,
        unreadCount,
        connected,
        latestToast,
        clearLatestToast,
        markRead,
        markAllRead,
        clearAll,
        refresh,
        subscribe,
    }), [items, unreadCount, connected, latestToast, clearLatestToast, markRead, markAllRead, clearAll, refresh, subscribe]);
    return (_jsx(NotificationsContext.Provider, { value: value, children: children }));
};
